import { useState, type CSSProperties } from 'react'
import { PhoneInput } from '../../../../packages/account/src/PhoneInput'
import { parkerPhoneProblem } from '../../../../packages/account/src/parkerPhone'
import { labelStyle } from '../ui'

/**
 * The phone field on Add parker and Edit parker.
 *
 * Adrian, 2026-10-09: "the phone number field should follow a controlled
 * state. Right now it allows numbers with no controls." The field was a bare
 * `<input>` that took any string; the only gate was a ten-digit count on the
 * submit button, with nothing on screen saying why the button was grey.
 *
 * It is the shared `PhoneInput` from `packages/account` — the one checkout,
 * /signin and the account pane use — imported by file path exactly as the
 * consumer does (`consumer/src/ui.tsx`), because the package's index pulls in
 * the whole account experience and this surface wants one input. That
 * component owns the as-you-type mask `(404) 555-0142`, the caret arithmetic
 * and the backspace-across-punctuation fixes; none of that is re-done here.
 * Read its header before changing how it is driven.
 *
 * What this adds on top is the parker RULE and how it is reported:
 *   - the value is always the formatted string; `normalizeParkerPhone` turns
 *     it into the ten digits the server stores (parkerPhone.ts);
 *   - the error is inline, under the field, and only after the manager has
 *     left the field or started typing — a blank form does not open covered
 *     in red;
 *   - `aria-invalid` + `aria-describedby` so the error is announced, and the
 *     hint is tied to the input for the same reason.
 * The submit button is still the caller's: it reads `normalizeParkerPhone`
 * itself, so the button cannot disagree with the message beneath the field.
 */
export function ParkerPhoneField({
  value, onChange, inputStyle, label = 'Phone', autoFocus,
}: {
  value: string
  onChange: (next: string) => void
  inputStyle: CSSProperties
  label?: string
  autoFocus?: boolean
}) {
  const [touched, setTouched] = useState(false)
  const problem = parkerPhoneProblem(value)
  // Shown once the field has been left, not while a number is half-typed: a
  // field that goes red at `(404` reads as broken, not as guidance. Clicking
  // the greyed submit button blurs the field, so the reason appears then too.
  const show = problem !== null && touched
  const errorId = 'parker-phone-error'
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <PhoneInput
        aria-label={label}
        aria-invalid={show || undefined}
        aria-describedby={show ? errorId : undefined}
        autoFocus={autoFocus}
        placeholder="(404) 555-0142"
        // Excess digits stay visible so the rule below refuses them; the
        // default mask would cut a pasted twelve-digit number to a valid-looking
        // ten and save someone else's phone.
        overflow="keep"
        value={value}
        onChange={onChange}
        onBlur={() => setTouched(true)}
        // The whole `border` shorthand, not `borderColor`: React warns when a
        // longhand is removed over a shorthand on re-render.
        style={{ ...inputStyle, ...(show ? { border: '1.5px solid #b91c1c' } : {}) }}
      />
      {show && (
        <div id={errorId} role="alert" style={{ fontSize: 12, color: '#b91c1c', marginTop: 5 }}>{problem}</div>
      )}
    </div>
  )
}
